import { externalUrl, externalUrlLabel } from "@/lib/externalUrl";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  useOrganization,
  useRemoveSteward,
} from "@/features/members/hooks/useOrganizations";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useMe } from "@/features/members/hooks/useMe";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LegacyMark } from "@/components/LegacyMark";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * An organization's own page — the thing many members are actually looking
 * for. Until now the directory card was the end of the road.
 *
 * Stewards get an edit link; everyone else gets the read view. The server
 * enforces that either way.
 */
export default function OrganizationPage() {
  const { id } = useParams();
  const { data: organization, isPending, error } = useOrganization(id);
  const { data: me } = useMe();
  // Unlinking somebody who has left. An administrator's, because the member
  // who moved on is exactly the one not coming back to do it themselves.
  const isAdmin = me?.member?.role === "admin";
  const removeSteward = useRemoveSteward(organization?.id);
  const [removing, setRemoving] = useState<{ id: number; name: string } | null>(null);

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>Organization not found</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          It may not exist, or it may be outside the directory preview. The
          directory is in the sidebar.
        </CardContent>
      </Card>
    );
  }

  const stewardsIt = organization.members.some((m) => m.id === me?.member?.id);

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <LegacyMark imageUrl={organization.image_url} name={organization.name} className="size-16" />
          <div>
            <h1 className="text-2xl">{organization.name}</h1>
            {organization.location && (
              <p className="text-muted-foreground text-sm">
                {organization.location}
              </p>
            )}
          </div>
        </div>
        {stewardsIt && (
          <Button variant="outline" asChild>
            <Link to="/organizations">Manage</Link>
          </Button>
        )}
      </div>

      {/* Under the header, as mediums sit under a steward's name on their page. */}
      {organization.mediums.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {organization.mediums.map((k) => (
            <Badge key={k} variant="outline">
              {k}
            </Badge>
          ))}
        </div>
      )}

      {organization.description && (
        <Card>
          <CardContent className="text-sm leading-relaxed">
            {organization.description}
          </CardContent>
        </Card>
      )}

      {externalUrl(organization.website) && (
        <Card>
          <CardContent>
            <a
              href={externalUrl(organization.website)!}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-sm underline-offset-4 hover:underline"
            >
              {externalUrlLabel(organization.website)}{" "}
              <ExternalLink className="size-3" />
            </a>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {organization.members.length === 1 ? "Steward" : "Stewards"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {organization.members.map((m) =>
            m.redacted ? (
              <div key={m.id} className="flex items-center gap-3">
                <Avatar className="size-9">
                  <AvatarImage src={m.image_url ?? undefined} alt="" />
                  <AvatarFallback>·</AvatarFallback>
                </Avatar>
                <span
                  aria-label="Steward available to full members"
                  className="bg-muted-foreground/25 block h-3 w-32 rounded-sm"
                />
              </div>
            ) : (
              <div key={m.id} className="flex items-center gap-3">
                <Link
                  to={`/directory/${m.id}`}
                  className="flex items-center gap-3 underline-offset-4 hover:underline"
                >
                  <Avatar className="size-9">
                    <AvatarImage src={m.image_url ?? undefined} alt={m.name} />
                    <AvatarFallback>{m.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span>
                    <span className="font-medium">{m.name}</span>
                    {m.title && (
                      <span className="text-muted-foreground text-sm">
                        {" "}
                        {m.title}
                      </span>
                    )}
                  </span>
                </Link>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-muted-foreground ml-auto"
                    onClick={() => setRemoving({ id: m.id, name: m.name })}
                  >
                    Remove
                  </Button>
                )}
                {/* No address here. What a steward is to this entity, and
                    which address to use for it, are both deliberately absent:
                    the first is asked for the administrator who reviews the
                    entry, and the second would be a second contact identity
                    running off one account. Write to them through their
                    member page. */}
              </div>
            )
          )}
          {organization.members.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No active stewards listed.
            </p>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={removing ? `Remove ${removing.name} as a steward?` : "Remove steward"}
        description={
          removing
            ? `${organization.name} stops appearing on their profile and in the directory under their name. The entry itself stays, and they are not told. If their membership is covered by it, they return to the free preview.`
            : ""
        }
        confirmLabel="Remove"
        destructive
        busy={removeSteward.isPending}
        onConfirm={() => {
          const target = removing;
          if (!target) return;
          removeSteward.mutate(
            { memberId: target.id },
            {
              onSuccess: () => {
                setRemoving(null);
                toast.success(`${target.name} no longer stewards ${organization.name}`);
              },
              onError: (err) => toast.error(err.message),
            }
          );
        }}
      />
    </div>
  );
}
